/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  isPearlApiDocumentVersionType,
  listToIdDict,
  PearlApiDocumentVersionSearchType,
  PearlApiDocumentVersionType,
  SlugInfoType,
} from './chatSlice';
import { documentVersionUpdateGeneratedFields } from './documentVersionGeneratedFieldsUtils';
import { fixupDocumentVersion, fixupPage } from './fixupDocumentAndPages';
import {
  DocumentVersionSearchType,
  DocumentVersionType,
  DocumentVersionWithoutGeneratedFieldsType,
  PageType,
} from '../algorithm/types';
import { nameToSlug, slugToName } from './searchSlice';
import { selectPredefinedWidgetsMap } from '../Fhir/fhirSlice';

/** Maps a slug name to a doc name and vice versa, using the autocomplete data.
 *
 *  Note - it relies on fetchAutoCompleteThunk to have been called before this is used.
 */
export function slugFoundCb(slugFound: SlugInfoType): SlugInfoType | undefined {
  if (!slugToName || !nameToSlug) {
    // didn't load from autocomplete
    console.log('slugFoundCb: slugToName or nameToSlug not loaded');
    return undefined;
  }
  if (slugFound.slug) {
    return {
      ...slugFound,
      slug: slugFound.slug,
      currentDocName: slugToName?.get(slugFound.slug) || slugFound.oldDocName,
    };
  }
  if (slugFound.docId) {
    // For embeddables we prioritise the docId, and it's okay to update the currentDocName from the slug,
    // but don't put the slug from the oldDocName
    return undefined;
  }
  if (slugFound.oldDocName) {
    if (nameToSlug?.has(slugFound.oldDocName)) {
      // console.log('found link to #' + slugFound.oldDocName, '=>', nameToSlug?.get(slugFound.oldDocName));
      return { ...slugFound, slug: nameToSlug?.get(slugFound.oldDocName), currentDocName: slugFound.oldDocName };
    } else {
      // Check if the oldDocName has the word 'referral' in it - if so, try removing that word
      const correctedOldDocName = slugFound.oldDocName.replace(/ referral /i, ' ').trim();
      if (nameToSlug?.has(correctedOldDocName)) {
        return {
          ...slugFound,
          slug: nameToSlug?.get(correctedOldDocName),
          currentDocName: correctedOldDocName,
        };
      }
      // console.log('link to #' + slugFound.oldDocName + ' not found', nameToSlug);
      return undefined;
    }
  }
  return undefined;
}

export async function convertPearlApiDocumentVersionToDocumentVersionType(
  data: PearlApiDocumentVersionType,
  disableFixup?: boolean // For testing
): Promise<DocumentVersionType>;
export async function convertPearlApiDocumentVersionToDocumentVersionType(
  data: PearlApiDocumentVersionSearchType,
  disableFixup?: boolean // For testing
): Promise<DocumentVersionSearchType>;
export async function convertPearlApiDocumentVersionToDocumentVersionType(
  data: PearlApiDocumentVersionType | PearlApiDocumentVersionSearchType,
  disableFixup?: boolean // For testing
): Promise<DocumentVersionType | DocumentVersionSearchType>;
export async function convertPearlApiDocumentVersionToDocumentVersionType(
  data: PearlApiDocumentVersionType | PearlApiDocumentVersionSearchType,
  disableFixup?: boolean // For testing
): Promise<DocumentVersionType | DocumentVersionSearchType> {
  if (!isPearlApiDocumentVersionType(data)) {
    return {
      ...data,
      audiences: {
        care_categories: data.audience?.care_categories?.map((x) => x.name) || [],
        patient_groups: data.audience?.patient_groups?.map((x) => x.name) || [],
        personas: data.audience?.personas?.map((x) => x.name) || [],
      },
    };
  }
  // Wait for the slug data to load first.  Hacky, sorry
  while (window.store.getState().search.titlesAndTriggers === null) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  const predefinedWidgetsMap = selectPredefinedWidgetsMap(window.store.getState());
  const owner = data.document?.members?.find((x) => x.role === 'OWNER');
  if (!Array.isArray(data.pages) && typeof data.pages === 'object') {
    throw new Error(
      'Document is already converted to DocumentVersionType in convertPearlApiDocumentVersionToDocumentVersionType'
    );
  }

  const pagesList_ = data.pages?.map((page) => {
    const page_ = {
      ...(page.content as Partial<PageType>),
      id: page.id,
      isRoot: page.isGuide || page.isRoot,
      name: page.name,
      document_version_id: data.id,
      meta: page.meta,
    } as PageType;
    if (disableFixup) {
      const isContextualized = page_.isContextualized;
      delete page_.isContextualized;
      page_.isContextualized = isContextualized; // Do this just to make the ordering match what fixupPage does
      return page_;
    }
    /** A wrapper around slugFoundCb  to check if this is a link to a page in this SAME document */
    function slugFoundCbForPage(slugFound: SlugInfoType): SlugInfoType | undefined {
      if (
        (slugFound.slug && data.doc_slug === slugFound.slug) ||
        (data.doc_slug && !slugFound.slug && slugFound.docId && data.document?.id === slugFound.docId) ||
        (data.doc_slug &&
          !slugFound.slug &&
          !slugFound.docId &&
          slugFound.oldDocName &&
          data.title === slugFound.oldDocName)
      ) {
        // This is a link to a page in the same document
        return { ...slugFound, slug: data.doc_slug, currentDocName: data.title, sameDocument: true };
      }
      return slugFoundCb(slugFound);
    }

    return fixupPage(page_, false, slugFoundCbForPage, undefined, predefinedWidgetsMap);
  });
  // Sanity check - check for any duplicate names in the pages list
  const pagesList = dedupePages(pagesList_);
  const flags = [...(data.flags || [])];
  const documentVersion: DocumentVersionWithoutGeneratedFieldsType = {
    createdAt: data.createdAt,
    id: data.id || data.document_version_id || '',
    assigneeId: data.assigneeId,
    pages: listToIdDict(pagesList),
    state: data.state,
    title: data.title,
    ...(data.snapshotName ? { snapshotName: data.snapshotName } : undefined),
    ...(data.createdFromId ? { createdFromId: data.createdFromId } : undefined),
    topics: data.topic_subtopic_pairs,
    lastModified: data.updatedAt,
    document: {
      docType: data.document?.docType || 'DOCUMENT',
      id: data.document?.id || '',
      members:
        data.document?.members
          ?.filter((x) => x.role !== 'OWNER')
          .map((x) => ({ id: x.userId, name: x.name, role: x.role, email: x.email })) || [],
      owner:
        owner && owner.userId && owner.name
          ? { id: owner.userId, name: owner.name }
          : { id: 'INVALID_MISSING', name: 'MISSING OWNER' }, // We found a real world case where a document was missing an owner
      library: data.document?.library!,
      slug: data.document?.slug || data.doc_slug, //doc_slug is from the autocomplete_rt endpoint
      doc_group: data.document?.doc_group,
    },
    tags: data.tags,
    flags,
    docCategories: data.docCategories || [],
    docCredits: convertDocCredits(data.docCredits),
    governanceModel: data.governanceModel ? data.governanceModel : undefined,
    likedByYou: data.likedByYou,
    bookmarkedByYou: data.bookmarkedByYou,
    likesCount: data.likesCount,
    audiences: {
      care_categories: data.audience.care_categories?.map((x) => x.name) || [],
      patient_groups: data.audience.patient_groups?.map((x) => x.name) || [],
      personas: data.audience.personas?.map((x) => x.name) || [],
    },
    library: data.library,
    metadata: data.metadata,
  };
  fixupDocumentVersion(documentVersion);
  documentVersionUpdateGeneratedFields(documentVersion);

  return documentVersion;
}

/** Check for any duplicate page names, and rename pages if found by appending a
 *  number to the name.
 */
function dedupePages(pagesList: PageType[]): PageType[] {
  // Make a map of the page names to the number of times they appear
  const pageNameCounts = pagesList.reduce((acc, page) => {
    acc[page.name] = (acc[page.name] || 0) + 1;
    return acc;
  }, {} as { [key: string]: number });
  // Now for every page name that appears more than once, rename the pages
  // to make them unique
  [...pagesList].reverse().forEach((page) => {
    if (pageNameCounts[page.name]! > 1) {
      let append = 1;
      let newName = '';
      do {
        append += 1;
        newName = `${page.name} (${append})`;
      } while (pageNameCounts[newName]);
      pageNameCounts[page.name!]! -= 1;
      page.name = newName;
      pageNameCounts[newName] = 1;
    }
  });
  return pagesList;
}

function convertDocCredits(docCredits?: PearlApiDocumentVersionType['docCredits']) {
  return (
    docCredits?.map((credit, creditIndex) => ({
      ...credit,
      id: credit.id ?? creditIndex.toString(),
      title:
        credit.title.toLowerCase() === 'last update'
          ? 'Published'
          : credit.title.toLowerCase() === 'next review'
          ? 'Next maintenance'
          : credit.title,
    })) || []
  );
}
